import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Copy, Trash2, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { toast, Spinner } from "../components/ui";
import { statusMeta, money } from "../lib/constants";
import { format } from "date-fns";

function pickSurvivor(items, selected) {
  const available = items.filter((i) => !selected.has(i.id));
  if (available.length === 0) return null;
  return available.reduce((best, cur) => {
    const bIsClient = best.client_type === "client" ? 0 : 1;
    const cIsClient = cur.client_type === "client" ? 0 : 1;
    if (bIsClient !== cIsClient) return bIsClient < cIsClient ? best : cur;
    const bData = (best.comment_count || 0) + (best.deposit_count || 0);
    const cData = (cur.comment_count || 0) + (cur.deposit_count || 0);
    if (bData !== cData) return bData > cData ? best : cur;
    const bTime = best.created_at || "";
    const cTime = cur.created_at || "";
    return bTime <= cTime ? best : cur;
  }, available[0]);
}

export default function DuplicatesTab() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: 50 });
      if (debounced) params.set("search", debounced);
      const r = await api.get("/clients/duplicates?" + params.toString());
      setData(r.data);
    } catch (e) {
      toast(e.response?.data?.detail || "Failed to load duplicates", "error");
    } finally {
      setLoading(false);
    }
  }, [debounced]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const mergeGroup = async (groupIdx) => {
    if (!data) return;
    const group = data.groups[groupIdx];
    const checked = group.items.filter((i) => selected.has(i.id)).map((i) => i.id);
    if (checked.length === 0) return;
    if (!window.confirm(`Merge and delete ${checked.length} record(s) into the kept one?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/clients/duplicates/merge", { remove_ids: checked });
      toast(`Merged ${r.data.merged} record(s) across ${r.data.groups_resolved} group(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast(e.response?.data?.detail || "Merge failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const mergeAll = async () => {
    if (!data) return;
    if (!window.confirm(`Merge and delete ALL duplicates across ${data.total_groups} groups? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/clients/duplicates/merge-all", {}, { timeout: 180000 });
      toast(`Merged ${r.data.merged} records across ${r.data.groups_resolved} groups`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast(e.response?.data?.detail || "Merge-all failed", "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return <div className="flex justify-center py-12"><Spinner className="w-6 h-6" /></div>;
  }

  if (!data) {
    return <div className="text-center text-sm text-slate-400 py-12">No duplicate groups found</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            className="input pl-9 w-64"
            placeholder="Search by email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="duplicates-search"
          />
        </div>
        <button
          className="btn-secondary text-rose-600 dark:text-rose-400"
          onClick={mergeAll}
          disabled={busy || data.total_groups === 0}
          data-testid="dup-merge-all"
        >
          <Trash2 className="w-4 h-4" />
          {busy ? <Spinner className="w-4 h-4" /> : "Delete all duplicates"}
        </button>
      </div>

      <div className="text-xs text-slate-500" data-testid="duplicates-summary">
        {data.total_groups} group(s) · {data.total_duplicate_records} extra record(s)
      </div>

      {data.groups.length === 0 && (
        <div className="text-center text-sm text-slate-400 py-8">No matching duplicate groups</div>
      )}

      {data.groups.map((group, gi) => {
        const survivor = pickSurvivor(group.items, selected);
        const checkedInGroup = group.items.filter((i) => selected.has(i.id)).length;
        return (
          <div key={gi} className="card p-4 space-y-3" data-testid={`duplicates-group-${gi}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                <Copy className="w-3.5 h-3.5 inline mr-1.5 text-slate-400" />
                {group.key_type === "email" ? "Email" : "Phone"}: <span className="font-mono">{group.key}</span>
                <span className="ml-2 text-xs text-slate-400">({group.count} records)</span>
              </div>
              {checkedInGroup > 0 && (
                <button
                  className="btn-primary text-xs py-1.5 px-3"
                  onClick={() => mergeGroup(gi)}
                  disabled={busy}
                  data-testid={`dup-group-merge-${gi}`}
                >
                  Merge &amp; delete checked ({checkedInGroup})
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="pb-2 pr-2 w-8"></th>
                    <th className="pb-2 pr-3">Name</th>
                    <th className="pb-2 pr-3">Type</th>
                    <th className="pb-2 pr-3">Status</th>
                    <th className="pb-2 pr-3">Email / Phone</th>
                    <th className="pb-2 pr-3">Comments</th>
                    <th className="pb-2 pr-3">Balance</th>
                    <th className="pb-2 pr-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => {
                    const isSurvivor = survivor?.id === item.id;
                    const sMeta = statusMeta(item.client_type, item.lead_status);
                    return (
                      <tr key={item.id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30" data-testid={`duplicate-row-${item.id}`}>
                        <td className="py-2 pr-2">
                          <input
                            type="checkbox"
                            checked={selected.has(item.id)}
                            onChange={() => toggle(item.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 accent-indigo-600"
                            data-testid={`dup-row-check-${item.id}`}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <Link to={`/${item.client_type === "client" ? "clients" : "leads"}/${item.id}`} className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline">
                            {item.name}
                          </Link>
                          {isSurvivor && (
                            <span className="ml-2 badge bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800 text-[10px]" data-testid={`dup-keep-${item.id}`}>
                              <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> Keep
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`badge ${item.client_type === "client" ? "bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-800" : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"}`}>
                            {item.client_type}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`badge ${sMeta?.cls || "bg-slate-100 text-slate-600 border-slate-200"}`}>{sMeta?.label || item.lead_status || "—"}</span>
                        </td>
                        <td className="py-2 pr-3 text-slate-500 text-xs">{item.email || item.phone || "—"}</td>
                        <td className="py-2 pr-3 text-center text-xs">{item.comment_count || 0}</td>
                        <td className="py-2 pr-3 text-xs">
                          {(item.balance || 0) > 0 ? <span className="text-emerald-600 dark:text-emerald-400 font-medium">{money(item.balance)}</span> : "—"}
                        </td>
                        <td className="py-2 pr-3 text-xs text-slate-400">{item.created_at ? format(new Date(item.created_at), "MMM d, yyyy") : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
